import { icons, type IconName } from './names';

/**
 * The app's single stroke weight.
 *
 * lucide ships at 2px on a 24 grid, which is the factory default every
 * scaffold puts in front of a reviewer — and the repo's own hand-drawn brand
 * glyphs (Cherish, SpreadIt, SignalMark) are drawn at 1.7px, so the two hands
 * were visibly different inside a single post's action row. 1.75 splits that
 * difference and is tuned to the body face's stem weight, so the glyphs sit at
 * the same optical density as the text beside them rather than out-weighing it.
 *
 * Forwarded from here so ~110 registered glyphs restyle from one edit; the
 * brand glyphs accept the same prop.
 */
export const ICON_STROKE = 1.75;

export interface IconProps {
  name: IconName;
  size?: number;
  title?: string;
  className?: string;
  /** Escape hatch for a glyph that needs a heavier stroke at a very small size. */
  strokeWidth?: number;
}

export function Icon({ name, size = 20, title, className, strokeWidth = ICON_STROKE }: IconProps) {
  const Glyph = icons[name];
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };
  return (
    <span className={className} style={{ display: 'inline-flex', lineHeight: 0 }} {...a11y}>
      <Glyph size={size} strokeWidth={strokeWidth} />
    </span>
  );
}
